import { useEffect, useRef, useState } from "react";
import { zoneOpacity, opponentColor, playerViewBasis, playerFacingBall } from "../lib/engine";
import { fitLabelText, layoutPlayerLabels } from "../lib/playerLabels";
import { Crosshair, LocateFixed, X } from "lucide-react";

const NEAR = 0.5;
const CAM_H = 1.7;
const TAU = Math.PI * 2;
const norm = (a) => ((a + Math.PI) % TAU + TAU) % TAU - Math.PI;
const clampN = (v, a, b) => Math.max(a, Math.min(b, v));

// First-person view from the selected player's eyes. Drag to look; tap a player (or the ball)
// to keep the camera locked on them while the play runs.
export default function PlayerView({ frame, focusId, teamColors, sport = "football", annotations = [], watchId = null, onWatch, gender = "boys" }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const sizeRef = useRef({ w: 800, h: 500 });
  const lookRef = useRef({ yaw: 0, pitch: 0.05 });
  const effRef = useRef({ yaw: 0, pitch: 0.05 });
  const drag = useRef(null);
  const hitsRef = useRef([]);
  const motionRef = useRef({});
  const labelLayoutRef = useRef([]);
  const lastFrameRef = useRef(null);
  const rafRef = useRef(null);
  const propsRef = useRef({});
  const [narrow, setNarrow] = useState(false);
  propsRef.current = { frame, focusId, teamColors, sport, annotations, watchId, gender };

  useEffect(() => {
    lookRef.current = { yaw: 0, pitch: 0.05 };
    effRef.current = { yaw: 0, pitch: 0.05 };
    motionRef.current = {};
    labelLayoutRef.current = [];
  }, [focusId]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => {
      const r = wrap.getBoundingClientRect();
      sizeRef.current = { w: Math.max(200, r.width), h: Math.max(160, r.height) };
      setNarrow(r.width < 640);
      draw();
    });
    ro.observe(wrap);
    return () => { ro.disconnect(); cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => { draw(); }, [frame, focusId, annotations, watchId, teamColors]); // eslint-disable-line

  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, yaw: effRef.current.yaw, pitch: effRef.current.pitch, moving: false };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.moving && Math.hypot(dx, dy) < 5) return;
    if (!d.moving) { d.moving = true; if (propsRef.current.watchId) onWatch?.(null); }
    const focal = sizeRef.current.w * 0.3;
    lookRef.current = { yaw: norm(d.yaw + dx / focal), pitch: clampN(d.pitch + dy / (focal * 1.4), -0.55, 0.65) };
    draw();
  };
  const onUp = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.moving) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const hit = hitsRef.current.find((h) => px >= h.x0 && px <= h.x1 && py >= h.y0 && py <= h.y1);
    if (hit) onWatch?.(hit.id === propsRef.current.watchId ? null : hit.id);
  };

  const recentre = () => {
    propsRef.current = { ...propsRef.current, watchId: null };
    lookRef.current = { yaw: 0, pitch: 0.05 };
    onWatch?.(null);
    draw();
  };

  function draw() {
    cancelAnimationFrame(rafRef.current);
    const canvas = canvasRef.current;
    const { frame, focusId, teamColors, sport, annotations, watchId, gender } = propsRef.current;
    if (!canvas || !frame) return;
    const { w, h } = sizeRef.current;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
    }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const focus = frame.entities.find((e) => e.id === focusId) || frame.entities[0];
    if (!focus) return;

    // ---- motion tracking (speed / stride phase / heading) ----
    const now = performance.now();
    if (lastFrameRef.current !== frame) {
      lastFrameRef.current = frame;
      const mm = motionRef.current;
      frame.entities.forEach((e) => {
        const prev = mm[e.id];
        if (prev) {
          const dt = (now - prev.time) / 1000;
          if (dt > 0 && dt < 0.5) {
            const dd = Math.hypot(e.x - prev.x, e.y - prev.y);
            const sp = dd / dt;
            prev.speed = prev.speed * 0.55 + sp * 0.45;
            prev.phase += dd * 2.1;
            if (dd > 0.01) { prev.vx = (e.x - prev.x) / dd; prev.vy = (e.y - prev.y) / dd; }
          } else prev.speed = 0;
          prev.x = e.x; prev.y = e.y; prev.time = now;
        } else mm[e.id] = { x: e.x, y: e.y, time: now, phase: Math.random() * TAU, speed: 0, vx: 0, vy: 1 };
      });
    }

    // ---- camera ----
    const dirUp = focus.team === "A" ? 1 : -1;
    const baseA = dirUp > 0 ? Math.PI / 2 : -Math.PI / 2;
    const cam = { x: focus.x, y: focus.y };
    let desired = lookRef.current;
    let target = null;
    if (watchId === "ball" && frame.ball) target = { x: frame.ball.x, y: frame.ball.y, z: frame.ball.carrierId ? 1.0 : frame.ball.z || 0.4 };
    else if (watchId) { const te = frame.entities.find((e) => e.id === watchId); if (te && te.id !== focus.id) target = { x: te.x, y: te.y, z: 1.2 }; }
    if (target) {
      const dx = target.x - cam.x, dy = target.y - cam.y, dist = Math.hypot(dx, dy);
      if (dist > 0.6) desired = { yaw: norm(Math.atan2(dy, dx) - baseA), pitch: clampN(-Math.atan2(target.z - CAM_H, dist) + 0.04, -0.38, 0.5) };
    }
    const eff = effRef.current;
    const dragging = !!drag.current?.moving;
    const k = dragging ? 0.35 : (target ? 0.2 : 0.22);
    const dyaw = norm(desired.yaw - eff.yaw), dpitch = desired.pitch - eff.pitch;
    eff.yaw = norm(eff.yaw + dyaw * k); eff.pitch += dpitch * k;
    if (target) lookRef.current = { yaw: eff.yaw, pitch: eff.pitch };
    const settled = !dragging && Math.abs(dyaw) < 0.0015 && Math.abs(dpitch) < 0.0015;

    const { fwd, right } = playerViewBasis(focus.team, eff.yaw);
    const focal = w * 0.72;
    const horizon = h * 0.46 - Math.tan(eff.pitch) * focal;

    const toCam = (wx, wy, z = 0) => { const dx = wx - cam.x, dy = wy - cam.y; return { d: dx * fwd.x + dy * fwd.y, l: dx * right.x + dy * right.y, z }; };
    const projCam = (c) => { const s = focal / c.d; return { x: w / 2 + c.l * s, y: horizon + (CAM_H - c.z) * s, scale: s, depth: c.d }; };
    const project = (wx, wy, z = 0) => { const c = toCam(wx, wy, z); return c.d < NEAR ? null : projCam(c); };
    const clipPoly = (cs) => {
      const out = [];
      for (let i = 0; i < cs.length; i++) {
        const p = cs[i], q = cs[(i + 1) % cs.length];
        const pin = p.d >= NEAR, qin = q.d >= NEAR;
        if (pin) out.push(p);
        if (pin !== qin) { const t = (NEAR - p.d) / (q.d - p.d); out.push({ d: NEAR, l: p.l + (q.l - p.l) * t, z: p.z + (q.z - p.z) * t }); }
      }
      return out;
    };
    const worldPoly = (pts, fill, stroke, lw = 1) => {
      const c = clipPoly(pts.map((p) => toCam(p.x, p.y, p.z || 0)));
      if (c.length < 3) return false;
      ctx.beginPath();
      c.forEach((cc, i) => { const p = projCam(cc); i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y); });
      ctx.closePath();
      if (fill) { ctx.fillStyle = fill; ctx.fill(); }
      if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = lw; ctx.stroke(); }
      return true;
    };
    const worldLine = (x1, y1, x2, y2, fill, widthM = 0.13) => {
      const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy) || 1;
      const nx = (-dy / len) * widthM / 2, ny = (dx / len) * widthM / 2;
      worldPoly([{ x: x1 + nx, y: y1 + ny }, { x: x2 + nx, y: y2 + ny }, { x: x2 - nx, y: y2 - ny }, { x: x1 - nx, y: y1 - ny }], fill);
    };

    // ---- sky ----
    const hz = clampN(horizon, -h, 2 * h);
    const sky = ctx.createLinearGradient(0, 0, 0, Math.max(1, hz));
    sky.addColorStop(0, "#070B14");
    sky.addColorStop(0.7, "#12293d");
    sky.addColorStop(1, "#2a5470");
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, Math.max(0, hz));
    // ground base to the horizon
    ctx.fillStyle = "#0b2a17";
    ctx.fillRect(0, Math.max(0, hz), w, h);

    // ---- run-off + pitch surface ----
    worldPoly([{ x: -40, y: -40 }, { x: 130, y: -40 }, { x: 130, y: 185 }, { x: -40, y: 185 }], "#0e3a1f");
    for (let s = 0; s < 29; s++) {
      const y0 = s * 5, y1 = y0 + 5;
      worldPoly([{ x: 0, y: y0 }, { x: 90, y: y0 }, { x: 90, y: y1 }, { x: 0, y: y1 }], s % 2 ? "#12603a" : "#0f5432");
    }
    // pitch lines (official markings)
    const LINE = "rgba(255,255,255,0.78)";
    worldLine(0, 0, 0, 145, LINE); worldLine(90, 0, 90, 145, LINE);
    [0, 13, 20, 45, 100, 125, 132, 145].forEach((yy) => worldLine(0, yy, 90, yy, LINE));
    for (let x = 0; x < 90; x += 4) worldLine(x, 72.5, Math.min(90, x + 2.2), 72.5, LINE);
    const worldArc = (cx, cy, r, a0, a1) => {
      const n = 16;
      for (let i = 0; i < n; i++) {
        const t0 = a0 + ((a1 - a0) * i) / n, t1 = a0 + ((a1 - a0) * (i + 1)) / n;
        worldLine(cx + Math.cos(t0) * r, cy + Math.sin(t0) * r, cx + Math.cos(t1) * r, cy + Math.sin(t1) * r, LINE);
      }
    };
    worldArc(45, 20, 13, 0, Math.PI); worldArc(45, 125, 13, Math.PI, TAU);
    worldArc(45, 20, 6.5, Math.PI, TAU); worldArc(45, 125, 6.5, 0, Math.PI);
    [0, 145].forEach((gy) => {
      const dir = gy === 0 ? 1 : -1;
      worldLine(35.5, gy, 35.5, gy + dir * 13, LINE); worldLine(54.5, gy, 54.5, gy + dir * 13, LINE); worldLine(35.5, gy + dir * 13, 54.5, gy + dir * 13, LINE);
      worldLine(38, gy, 38, gy + dir * 4.5, LINE); worldLine(52, gy, 52, gy + dir * 4.5, LINE); worldLine(38, gy + dir * 4.5, 52, gy + dir * 4.5, LINE);
    });

    // ---- space zones ----
    (annotations || []).filter((an) => an.type === "zone").forEach((z) => {
      const op = zoneOpacity(z, frame.entities);
      const pts = [];
      for (let i = 0; i < 36; i++) { const th = (i / 36) * TAU; pts.push({ x: z.x + Math.cos(th) * z.r, y: z.y + Math.sin(th) * z.r }); }
      worldPoly(pts, `rgba(45,212,191,${op})`, `rgba(94,234,212,${Math.min(0.8, op + 0.3)})`, 1.5);
    });

    // ---- stadium surround (orientation cue) ----
    const SX0 = -16, SX1 = 106, SY0 = -16, SY1 = 161;
    const walls = [
      [{ x: SX0, y: SY0 }, { x: SX1, y: SY0 }], [{ x: SX1, y: SY0 }, { x: SX1, y: SY1 }],
      [{ x: SX1, y: SY1 }, { x: SX0, y: SY1 }], [{ x: SX0, y: SY1 }, { x: SX0, y: SY0 }],
    ];
    walls.forEach(([p, q]) => {
      const len = Math.hypot(q.x - p.x, q.y - p.y);
      const segs = Math.ceil(len / 9);
      for (let i = 0; i < segs; i++) {
        const f0 = i / segs, f1 = (i + 1) / segs;
        const a0 = { x: p.x + (q.x - p.x) * f0, y: p.y + (q.y - p.y) * f0 };
        const a1 = { x: p.x + (q.x - p.x) * f1, y: p.y + (q.y - p.y) * f1 };
        worldPoly([{ ...a0, z: 0 }, { ...a1, z: 0 }, { ...a1, z: 2.4 }, { ...a0, z: 2.4 }], "#1d2230");
        worldPoly([{ ...a0, z: 2.4 }, { ...a1, z: 2.4 }, { ...a1, z: 9 }, { ...a0, z: 9 }], i % 2 ? "#3a4256" : "#2f3647");
        worldPoly([{ ...a0, z: 9 }, { ...a1, z: 9 }, { ...a1, z: 10 }, { ...a0, z: 10 }], "#12161f");
      }
    });
    [[-22, -22], [112, -22], [-22, 167], [112, 167]].forEach(([fx, fy]) => {
      const b = project(fx, fy, 0), t = project(fx, fy, 30);
      if (b && t) {
        ctx.strokeStyle = "rgba(200,205,220,0.7)"; ctx.lineWidth = Math.max(1, b.scale * 0.5);
        ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(t.x, t.y); ctx.stroke();
        ctx.save(); ctx.shadowColor = "rgba(255,244,200,0.9)"; ctx.shadowBlur = 18; ctx.fillStyle = "#fff6d5";
        ctx.fillRect(t.x - Math.max(3, t.scale * 2), t.y - Math.max(2, t.scale * 1.2), Math.max(6, t.scale * 4), Math.max(4, t.scale * 2.4)); ctx.restore();
      }
    });

    // ---- goals (both ends) ----
    const drawGoal = (goalY) => {
      const post = (px) => {
        const base = project(px, goalY, 0), top = project(px, goalY, 8);
        if (base && top) { ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = Math.max(1.4, base.scale * 0.13); ctx.beginPath(); ctx.moveTo(base.x, base.y); ctx.lineTo(top.x, top.y); ctx.stroke(); }
      };
      post(41.75); post(48.25);
      const cb1 = project(41.75, goalY, 2.5), cb2 = project(48.25, goalY, 2.5);
      if (cb1 && cb2) { ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = Math.max(1.2, cb1.scale * 0.11); ctx.beginPath(); ctx.moveTo(cb1.x, cb1.y); ctx.lineTo(cb2.x, cb2.y); ctx.stroke(); }
      const dir = goalY === 0 ? -1 : 1;
      worldPoly([{ x: 41.75, y: goalY, z: 0 }, { x: 48.25, y: goalY, z: 0 }, { x: 48.25, y: goalY + dir * 2.2, z: 0 }, { x: 41.75, y: goalY + dir * 2.2, z: 0 }], "rgba(255,255,255,0.08)", "rgba(255,255,255,0.35)", 1);
      worldPoly([{ x: 41.75, y: goalY, z: 2.5 }, { x: 48.25, y: goalY, z: 2.5 }, { x: 48.25, y: goalY + dir * 2.2, z: 0 }, { x: 41.75, y: goalY + dir * 2.2, z: 0 }], "rgba(255,255,255,0.07)");
    };
    drawGoal(145); drawGoal(0);

    // ---- players + ball, far to near ----
    const homeColor = teamColors?.primary || "#0EA5E9";
    const awayColor = opponentColor(homeColor);
    hitsRef.current = [];
    const list = frame.entities
      .filter((e) => e.id !== focus.id)
      .map((e) => ({ e, p: project(e.x, e.y) }))
      .filter((o) => o.p)
      .sort((x, y) => y.p.depth - x.p.depth);
    const ballCarried = frame.ball?.carrierId && frame.ball.carrierId !== focus.id;
    const ballR = (s) => Math.max(2.5, s * (sport === "hurling" ? 0.1 : 0.16));
    const drawBall = (bx, by, bz, glow = true) => {
      const b = project(bx, by, bz);
      if (!b) return;
      const r = ballR(b.scale);
      const gb = project(bx, by, 0);
      if (gb) { ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.beginPath(); ctx.ellipse(gb.x, gb.y, r * 0.9, r * 0.35, 0, 0, TAU); ctx.fill(); }
      ctx.save();
      if (glow) { ctx.shadowColor = "rgba(250,204,21,0.8)"; ctx.shadowBlur = 10; }
      const g = ctx.createRadialGradient(b.x - r * 0.3, b.y - r * 0.3, r * 0.1, b.x, b.y, r);
      g.addColorStop(0, "#fff3b0"); g.addColorStop(1, sport === "hurling" ? "#e9e3c8" : "#e0b010");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(b.x, b.y, r, 0, TAU); ctx.fill();
      ctx.restore();
      if (r > 4) { ctx.strokeStyle = "rgba(60,40,0,0.45)"; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(b.x, b.y, r * 0.98, 0.4, 2.7); ctx.stroke(); }
      hitsRef.current.unshift({ id: "ball", x0: b.x - Math.max(10, r), x1: b.x + Math.max(10, r), y0: b.y - Math.max(10, r), y1: b.y + Math.max(10, r) });
    };

    const nameLabels = layoutHomeLabels(ctx, list, w, h, watchId, labelLayoutRef.current);
    labelLayoutRef.current = nameLabels;
    drawLabelConnectors(ctx, nameLabels, watchId);
    for (const { e, p } of list) {
      const color = e.team === "A" ? homeColor : awayColor;
      const m = motionRef.current[e.id] || { speed: 0, phase: 0, vx: 0, vy: 1 };
      const carrier = frame.ball?.carrierId === e.id;
      const watched = watchId === e.id;
      const carrierHeading = carrier && m.speed > 0.1 ? { x: m.vx, y: m.vy, elevation: 0 } : m.facing;
      m.facing = playerFacingBall(e, frame.ball, carrierHeading);
      drawPlayer(ctx, p, e, color, m, { fwd, right, facing: m.facing, carrier, watched, haze: clampN(1 - p.depth / 230, 0.45, 1), female: gender === "girls" });
      const u = p.scale;
      hitsRef.current.unshift({ id: e.id, x0: p.x - Math.max(12, u * 0.6), x1: p.x + Math.max(12, u * 0.6), y0: p.y - Math.max(26, u * 2.1), y1: p.y + 4 });
      if (carrier && ballCarried) drawBall(e.x + right.x * 0.02, e.y + right.y * 0.02, 1.02, false);
    }
    if (frame.ball && !ballCarried) drawBall(frame.ball.x, frame.ball.y, frame.ball.carrierId ? 1.0 : frame.ball.z || 0.4);
    drawHomeLabels(ctx, nameLabels, watchId, hitsRef.current);

    // ---- radar / compass ----
    drawRadar(ctx, w, h, { frame, focus, fwd, right, focal, homeColor, awayColor, watchId, dirUp, yaw: eff.yaw });

    if (!settled) rafRef.current = requestAnimationFrame(draw);
  }

  const watchedName = (() => {
    if (!watchId) return null;
    if (watchId === "ball") return "the ball";
    const e = frame?.entities.find((x) => x.id === watchId);
    return e ? `${e.team === "B" ? "Opp " : ""}#${e.number} ${e.name || ""}` : null;
  })();
  const stop = (e) => e.stopPropagation();

  return (
    <div ref={wrapRef} className="relative h-full w-full overflow-hidden bg-[#070B14]" style={{ touchAction: "none", cursor: "grab" }}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp}
      data-testid="player-view-wrap">
      <canvas ref={canvasRef} data-testid="player-view-canvas" className="block select-none" />
      <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1.5" onPointerDown={stop} onPointerUp={stop}>
        <button data-testid="pv-recentre" onClick={recentre} className="flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/55 hover:bg-black/75 border border-white/15 text-white/85 rounded-full px-2.5 py-1"><LocateFixed className="h-3 w-3" />{!narrow && "Recentre"}</button>
        <button data-testid="pv-watch-ball" onClick={() => onWatch?.(watchId === "ball" ? null : "ball")} className={`flex items-center gap-1 text-[10px] uppercase tracking-wider border rounded-full px-2.5 py-1 ${watchId === "ball" ? "bg-[#FACC15] text-black border-[#FACC15]" : "bg-black/55 hover:bg-black/75 border-white/15 text-white/85"}`}><Crosshair className="h-3 w-3" />{narrow ? "Ball" : "Watch ball"}</button>
      </div>
      <div className={`absolute z-10 flex items-center gap-1.5 ${narrow ? "bottom-10 left-2" : "bottom-2 left-1/2 -translate-x-1/2"}`} onPointerDown={stop} onPointerUp={stop}>
        <div className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 flex items-center gap-1.5"
          style={{ background: watchedName ? "rgba(0,240,255,0.16)" : "rgba(0,0,0,0.35)", color: watchedName ? "#00F0FF" : "rgba(255,255,255,0.55)", border: watchedName ? "1px solid rgba(0,240,255,0.5)" : "1px solid transparent" }}
          data-testid="pv-status">
          {watchedName ? <>Watching {watchedName}</> : narrow ? <>Tap a player to watch</> : <>Drag to look · tap a player to watch them</>}
        </div>
        {watchedName && (
          <button data-testid="pv-stop-watch" onClick={() => onWatch?.(null)} className="h-6 w-6 rounded-full bg-black/60 border border-white/15 text-white/80 flex items-center justify-center" title="Free look">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- humanoid player (billboard) ----------
function drawPlayer(ctx, p, e, color, m, { fwd, right, facing, carrier, watched, haze, female }) {
  const u = p.scale; // px per metre
  const fx = p.x, fy = p.y;
  const Y = (z) => fy - z * u;
  const X = (mx) => fx + mx * u;
  const skin = "#d9a577";
  const light = luminance(color) > 0.6;
  const shorts = light ? "#111827" : "#f3f4f6";
  const socks = shorts;
  const outline = "rgba(0,0,0,0.4)";
  const H = 1.85 * u;

  ctx.globalAlpha = haze;
  // shadow + rings
  ctx.fillStyle = "rgba(0,0,0,0.38)";
  ctx.beginPath(); ctx.ellipse(fx, fy, u * 0.5, u * 0.16, 0, 0, TAU); ctx.fill();
  if (carrier) { ctx.strokeStyle = "#FACC15"; ctx.lineWidth = Math.max(1.5, u * 0.07); ctx.beginPath(); ctx.ellipse(fx, fy, u * 0.62, u * 0.22, 0, 0, TAU); ctx.stroke(); }
  if (watched) { ctx.strokeStyle = "#00F0FF"; ctx.lineWidth = Math.max(1.5, u * 0.06); ctx.setLineDash([Math.max(3, u * 0.2), Math.max(3, u * 0.15)]); ctx.beginPath(); ctx.ellipse(fx, fy, u * 0.78, u * 0.28, 0, 0, TAU); ctx.stroke(); ctx.setLineDash([]); }

  if (H < 7) {
    // far away: simple capsule
    ctx.fillStyle = color; roundRect(ctx, fx - u * 0.25, Y(1.6), u * 0.5, u * 1.55, u * 0.2); ctx.fill();
    ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(fx, Y(1.72), Math.max(1, u * 0.13), 0, TAU); ctx.fill();
    ctx.globalAlpha = 1;
    return;
  }

  // Running stride follows the path; torso and head keep tracking the ball.
  const amp = clampN(m.speed / 5, 0, 1);
  const lat = Math.abs(m.vx * right.x + m.vy * right.y);
  const faceSide = facing.x * right.x + facing.y * right.y;
  const faceDepth = facing.x * fwd.x + facing.y * fwd.y;
  const away = faceDepth > 0.1;
  const side = amp > 0.05 ? clampN(lat, 0.35, 1) : 0;
  const s = Math.sin(m.phase) * amp;
  const wf = 1 - 0.45 * Math.abs(faceSide);
  const lw = (mm) => Math.max(1, mm * u);
  const stroke = (x1, y1, x2, y2, col, width) => { ctx.strokeStyle = col; ctx.lineWidth = width; ctx.lineCap = "round"; ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); };
  const limb = (x1, y1, x2, y2, col, width) => { stroke(x1, y1, x2, y2, outline, width + Math.max(1, u * 0.03)); stroke(x1, y1, x2, y2, col, width); };

  // legs (back leg first)
  const leg = (sign, swing) => {
    const hipX = X(sign * 0.09 * wf), hipY = Y(0.86);
    const lift = Math.max(0, -swing * sign) * 0.16;
    const footX = X(sign * 0.1 * wf + swing * side * 0.28), footY = Y(0.06 + lift * amp);
    const kneeX = (hipX + footX) / 2 + swing * side * 0.08 * u, kneeY = Y(0.47 + lift * 0.6);
    limb(hipX, hipY, kneeX, kneeY, skin, lw(0.17));
    limb(kneeX, kneeY, footX, footY, skin, lw(0.14));
    // sock + boot
    const sx0 = kneeX + (footX - kneeX) * 0.35, sy0 = kneeY + (footY - kneeY) * 0.35;
    stroke(sx0, sy0, footX, footY, socks, lw(0.15));
    ctx.fillStyle = "#0b0b0f"; roundRect(ctx, footX - u * 0.11, footY - u * 0.06, u * 0.24, u * 0.09, u * 0.04); ctx.fill();
  };
  leg(-1, s); leg(1, -s);

  // shorts
  ctx.fillStyle = outline; roundRect(ctx, X(-0.27 * wf) - 1, Y(1.03), 0.54 * wf * u + 2, 0.26 * u + 1, u * 0.08); ctx.fill();
  ctx.fillStyle = shorts; roundRect(ctx, X(-0.27 * wf), Y(1.03), 0.54 * wf * u, 0.26 * u, u * 0.08); ctx.fill();

  // arms
  const arm = (sign, swing) => {
    const shX = X(sign * 0.29 * wf), shY = Y(1.5);
    let elX, elY, hdX, hdY;
    if (carrier) { elX = X(sign * 0.33 * wf); elY = Y(1.18); hdX = X(sign * 0.1); hdY = Y(1.02); }
    else { elX = X(sign * 0.34 * wf + swing * side * 0.1); elY = Y(1.2 + swing * 0.06); hdX = X(sign * 0.3 * wf + swing * side * 0.22); hdY = Y(0.95 + Math.max(0, swing) * 0.25 * amp); }
    limb(shX, shY, elX, elY, color, lw(0.14));
    limb(elX, elY, hdX, hdY, skin, lw(0.11));
  };
  arm(-1, -s); arm(1, s);

  // jersey
  const jx = X(-0.3 * wf), jy = Y(1.56), jw = 0.6 * wf * u, jh = 0.56 * u;
  ctx.fillStyle = outline; roundRect(ctx, jx - 1, jy - 1, jw + 2, jh + 2, u * 0.1); ctx.fill();
  const g = ctx.createLinearGradient(jx, 0, jx + jw, 0);
  g.addColorStop(0, shade(color, 0.12)); g.addColorStop(0.55, color); g.addColorStop(1, shade(color, -0.22));
  ctx.fillStyle = g; roundRect(ctx, jx, jy, jw, jh, u * 0.1); ctx.fill();
  ctx.fillStyle = "rgba(0,0,0,0.18)"; ctx.fillRect(jx, Y(1.3), jw, u * 0.05);
  if (!away) {
    ctx.fillStyle = light ? "#111827" : "#f3f4f6"; ctx.beginPath(); ctx.moveTo(X(-0.08 * wf), Y(1.56)); ctx.lineTo(X(0.08 * wf), Y(1.56)); ctx.lineTo(fx, Y(1.47)); ctx.closePath(); ctx.fill();
  }

  // Front, profile and back-of-head cues make ball-facing visible even when stationary.
  stroke(fx, Y(1.56), fx, Y(1.62), skin, lw(0.1));
  const hr = u * 0.13;
  const headY = Y(1.72);
  const eyeY = headY - clampN(facing.elevation || 0, -0.6, 0.8) * hr * 0.5;
  ctx.fillStyle = outline; ctx.beginPath(); ctx.arc(fx, headY, hr + 1, 0, TAU); ctx.fill();
  ctx.fillStyle = away ? "#2b1d12" : skin; ctx.beginPath(); ctx.arc(fx, headY, hr, 0, TAU); ctx.fill();
  if (Math.abs(faceSide) > 0.3) {
    ctx.fillStyle = skin; ctx.beginPath();
    ctx.moveTo(fx + faceSide * hr * 0.55, eyeY - hr * 0.4);
    ctx.lineTo(fx + faceSide * hr * 1.4, eyeY + hr * 0.2);
    ctx.lineTo(fx + faceSide * hr * 0.55, eyeY + hr * 0.65);
    ctx.fill();
  }
  if (!away) {
    ctx.fillStyle = "#2b1d12"; ctx.beginPath(); ctx.arc(fx, headY - hr * 0.08, hr * 1.02, Math.PI * 1.05, Math.PI * 1.95); ctx.fill();
    ctx.fillStyle = "#171717";
    for (const sign of [-1, 1]) {
      ctx.beginPath(); ctx.arc(fx + faceSide * hr * 0.5 + sign * hr * 0.34 * wf, eyeY, Math.max(0.5, hr * 0.11), 0, TAU); ctx.fill();
    }
  }
  if (female) {
    const swing = Math.sin(m.phase * 0.5) * amp * 0.06;
    const backX = fx - faceSide * hr * 0.95;
    ctx.fillStyle = "#2b1d12"; ctx.beginPath(); ctx.arc(backX, Y(1.8), hr * 0.45, 0, TAU); ctx.fill();
    stroke(backX, Y(1.78), backX + swing * u - faceSide * hr * 0.4, Y(1.45), "#2b1d12", lw(0.07));
  }

  // number on jersey
  if (u > 9) {
    ctx.fillStyle = readableText(color);
    ctx.font = `800 ${Math.max(7, u * 0.3)}px Outfit, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(String(e.number), fx, Y(1.27));
    ctx.textBaseline = "alphabetic";
  }
  ctx.globalAlpha = 1;
}

function layoutHomeLabels(ctx, list, w, h, watchId, previous) {
  const visible = list.filter(({ p }) => p.x >= 0 && p.x <= w && p.y >= 0 && p.y - 2 * p.scale < h);
  const obstacles = visible.map(({ p }) => ({ x: p.x - p.scale * 0.45, y: p.y - p.scale * 1.9, width: p.scale * 0.9, height: p.scale * 1.9 }));
  const radarSize = w < 640 ? 100 : 140;
  obstacles.push({ x: w - radarSize, y: h - radarSize, width: radarSize, height: radarSize });
  const names = visible.filter(({ e, p }) => e.team === "A" && e.name?.trim() && p.scale > 5.5)
    .sort((a, b) => Number(b.e.id === watchId) - Number(a.e.id === watchId) || a.p.depth - b.p.depth)
    .map(({ e, p }) => {
      const fontSize = clampN(p.scale * 0.24, 11, 14);
      ctx.font = `600 ${fontSize}px Outfit, sans-serif`;
      const fitted = fitLabelText(e.name, (text) => ctx.measureText(text).width, Math.min(160, w - 36));
      return { id: e.id, ...fitted, fontSize, width: fitted.width + 12, height: fontSize + 8,
        anchorX: p.x, anchorY: p.y - p.scale * 1.92, offset: fontSize / 2 + 10, above: true };
    });
  return layoutPlayerLabels(names, obstacles, { x: 8, y: 64, width: w - 16, height: Math.max(0, h - 120) }, 4, previous);
}

function drawLabelConnectors(ctx, labels, watchId) {
  ctx.save();
  labels.forEach((label) => {
    ctx.beginPath(); ctx.moveTo(label.anchorX, label.anchorY); ctx.lineTo(label.lineX, label.lineY);
    ctx.strokeStyle = "rgba(4,10,16,0.8)"; ctx.lineWidth = 3; ctx.stroke();
    ctx.strokeStyle = label.id === watchId ? "#00F0FF" : "rgba(255,255,255,0.85)";
    ctx.lineWidth = 1.2; ctx.stroke();
  });
  ctx.restore();
}

function drawHomeLabels(ctx, labels, watchId, hits) {
  ctx.save();
  labels.forEach((label) => {
    ctx.fillStyle = "rgba(10,13,20,0.94)";
    roundRect(ctx, label.x, label.y, label.width, label.height, 4); ctx.fill();
    ctx.strokeStyle = label.id === watchId ? "#00F0FF" : "rgba(255,255,255,0.3)";
    ctx.stroke();
    ctx.font = `600 ${label.fontSize}px Outfit, sans-serif`;
    ctx.fillStyle = "#FFFFFF"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(label.text, label.x + label.width / 2, label.y + label.height / 2);
    hits.unshift({ id: label.id, x0: label.x, y0: label.y, x1: label.x + label.width, y1: label.y + label.height });
  });
  ctx.restore();
}

// ---------- radar / compass ----------
function drawRadar(ctx, w, h, { frame, focus, fwd, right, focal, homeColor, awayColor, watchId, dirUp, yaw }) {
  const R = w < 640 ? 34 : Math.min(52, Math.max(36, w * 0.06));
  const cx = w - R - 14, cy = h - R - 14;
  const range = 55, sc = R / range;
  ctx.save();
  ctx.fillStyle = "rgba(8,11,18,0.78)";
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, TAU); ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.18)"; ctx.lineWidth = 1; ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy, R / 2, 0, TAU); ctx.stroke();
  // field of view wedge
  const half = Math.atan((w / 2) / focal);
  ctx.fillStyle = "rgba(0,240,255,0.12)";
  ctx.beginPath(); ctx.moveTo(cx, cy); ctx.arc(cx, cy, R, -Math.PI / 2 - half, -Math.PI / 2 + half); ctx.closePath(); ctx.fill();
  const rel = (x, y) => { const dx = x - focus.x, dy = y - focus.y; const d = dx * fwd.x + dy * fwd.y, l = dx * right.x + dy * right.y; const dist = Math.hypot(d, l); const f = dist > range ? range / dist : 1; return { x: cx + l * sc * f, y: cy - d * sc * f, clipped: dist > range }; };
  frame.entities.forEach((e) => {
    if (e.id === focus.id) return;
    const q = rel(e.x, e.y);
    ctx.globalAlpha = q.clipped ? 0.4 : 1;
    ctx.fillStyle = e.team === "A" ? homeColor : awayColor;
    ctx.beginPath(); ctx.arc(q.x, q.y, 2.6, 0, TAU); ctx.fill();
    if (watchId === e.id) { ctx.strokeStyle = "#00F0FF"; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(q.x, q.y, 5, 0, TAU); ctx.stroke(); }
  });
  ctx.globalAlpha = 1;
  if (frame.ball) { const q = rel(frame.ball.x, frame.ball.y); ctx.fillStyle = "#FACC15"; ctx.beginPath(); ctx.arc(q.x, q.y, 2.4, 0, TAU); ctx.fill(); if (watchId === "ball") { ctx.strokeStyle = "#00F0FF"; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(q.x, q.y, 5, 0, TAU); ctx.stroke(); } }
  // you
  ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.moveTo(cx, cy - 5); ctx.lineTo(cx - 4, cy + 4); ctx.lineTo(cx + 4, cy + 4); ctx.closePath(); ctx.fill();
  // attacking goal marker on the rim
  const g = { x: 45, y: dirUp > 0 ? 145 : 0 };
  const gdx = g.x - focus.x, gdy = g.y - focus.y;
  const gd = gdx * fwd.x + gdy * fwd.y, gl = gdx * right.x + gdy * right.y;
  const ang = Math.atan2(-gd, gl);
  const mx = cx + Math.cos(ang) * (R + 8), my = cy + Math.sin(ang) * (R + 8);
  ctx.fillStyle = "#00F0FF"; ctx.beginPath(); ctx.arc(mx, my, 3.2, 0, TAU); ctx.fill();
  ctx.font = "700 9px Inter, sans-serif"; ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.textAlign = "center";
  const ay = Math.abs(yaw);
  const facing = ay < 0.5 ? "FACING OPP GOAL" : ay > 2.6 ? "FACING OWN GOAL" : yaw > 0 ? "LOOKING RIGHT" : "LOOKING LEFT";
  ctx.fillText(facing, cx, cy - R - 12);
  ctx.restore();
}

function shade(hex, amt) {
  const { r, g, b } = toRgb(hex);
  const f = (v) => Math.max(0, Math.min(255, Math.round(v + 255 * amt)));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

function toRgb(hex) {
  let h = String(hex).replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return { r: parseInt(h.slice(0, 2), 16) || 0, g: parseInt(h.slice(2, 4), 16) || 0, b: parseInt(h.slice(4, 6), 16) || 0 };
}

function luminance(hex) {
  const { r, g, b } = toRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function readableText(hex) {
  return luminance(hex) > 0.6 ? "#0b1220" : "#ffffff";
}

function roundRect(ctx, x, y, w, h, r) {
  if (![x, y, w, h].every((v) => Number.isFinite(v))) return;
  w = Math.max(0.5, w);
  h = Math.max(0.5, h);
  r = Number.isFinite(r) ? Math.max(0, Math.min(r, w / 2, h / 2)) : 0;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
